import * as Yup from 'yup'

const CHECKSUM_CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ012345';

/**
 * Validates that an 18-character Salesforce ID has correctly formatted checksum characters.
 *
 * The checksum (characters 16–18) is derived by splitting the first 15 characters into
 * three 5-character chunks. For each chunk, uppercase letters are treated as 1 and all
 * other characters as 0, forming a 5-bit binary number (read right to left). Each binary
 * number maps to a character in the charset A–Z, 0–5.
 *
 * @param {string} id - The 18-character Salesforce ID to validate.
 * @returns {boolean} True if the checksum characters are valid, false otherwise.
 */
export function isValidSalesforceIdChecksum(id) {
  if (typeof id !== 'string' || id.length !== 18) {
    return false;
  }

  // Verify entire ID is alphanumeric
  if (!/^[a-zA-Z0-9]{18}$/.test(id)) {
    return false;
  }

  const baseId = id.substring(0, 15);
  const providedChecksum = id.substring(15, 18);
  const computedChecksum = computeChecksumSuffix(baseId);

  return providedChecksum === computedChecksum;
}

/**
 * Computes the 3-character checksum suffix for a 15-character Salesforce ID.
 *
 * @param {string} baseId - The first 15 characters of a Salesforce ID.
 * @returns {string} The 3-character checksum suffix.
 */
function computeChecksumSuffix(baseId) {
  let checksum = '';

  for (let chunk = 0; chunk < 3; chunk++) {
    let value = 0;

    for (let i = 0; i < 5; i++) {
      const char = baseId[chunk * 5 + i];

      // Uppercase letters contribute a 1 bit at position i
      if (char >= 'A' && char <= 'Z') {
        value += 1 << i;
      }
    }

    checksum += CHECKSUM_CHARSET[value];
  }

  return checksum;
}

/**
 * Reusable Yup validation schema for an optional 18-character Salesforce ID field.
 *
 * Validation order:
 * 1. Trims leading/trailing whitespace
 * 2. Allows empty/blank values (field is optional)
 * 3. Checks for exactly 18 characters
 * 4. Checks that all characters are alphanumeric
 * 5. Validates the checksum suffix conforms to the Salesforce ID format
 *
 * @returns {Yup.StringSchema} A Yup string schema for Salesforce ID validation.
 */
export const salesforceIdSchema = Yup.string()
  .trim()
  .test(
    'salesforce-id-length',
    'Salesforce ID must be exactly 18 characters',
    value => !value || value.length === 18
  )
  .test(
    'salesforce-id-alphanumeric',
    'Salesforce ID may only contain letters and numbers',
    value => !value || /^[a-zA-Z0-9]+$/.test(value)
  )
  .test(
    'salesforce-id-checksum',
    'Salesforce ID does not match a valid Salesforce ID format',
    value => !value || isValidSalesforceIdChecksum(value)
  )
